import React, { useMemo, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import DashboardCard from './DashboardCard';
import { getTodayOnLeave } from '../../../services/leaveDashboardService';
import { heatColor, HEAT_RAMP, HEAT_EMPTY, STATUS, TYPE_FOLD_COLOR } from '../constants';
import {
  ListRow, RowMain, RowName, RowSub, TypeChip, Footnote,
} from '../styles';
import { initialsOf } from './TodayOnLeaveCard';
import RowAvatar from './RowAvatar';

const LegendWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  color: #6B7280;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const TodayPill = styled.span`
  padding: 2px 8px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.primary.main};
  color: #fff;
  font-weight: 700;
`;

const RampSwatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
  background: ${({ $color }) => $color};
`;

const LegendKey = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const HatchSwatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
  background: repeating-linear-gradient(45deg, rgba(237, 161, 0, 0.7) 0 2px, #fff 2px 4px);
  border: 1px solid #eda100;
`;

const Strip = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const DayCol = styled.button`
  width: 26px;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;

const DayNum = styled.div`
  font-size: 10px;
  font-weight: ${({ $today }) => ($today ? 800 : 600)};
  color: ${({ $muted, $today, theme }) => ($today ? theme.colors.primary.main : $muted ? '#C7CCD4' : '#9CA3AF')};
  text-align: center;
  margin-bottom: 4px;
`;

const Cell = styled.div`
  height: 26px;
  border-radius: 4px;
  background: ${({ $bg }) => $bg};
  position: relative;
  ${({ $today, theme }) => $today && css`outline: 2px solid ${theme.colors.primary.main}; outline-offset: 1px;`}
  ${({ $pending }) => $pending && css`
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 4px;
      background: repeating-linear-gradient(45deg, rgba(237, 161, 0, 0.55) 0 3px, transparent 3px 6px);
    }
  `}
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const DayPanel = styled.div`
  margin-top: 12px;
  border: 1px solid #E5EAF0;
  border-radius: 6px;
  padding: 12px 14px;
`;

const DayPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;

  button {
    border: none;
    background: none;
    cursor: pointer;
    color: #9CA3AF;
    display: flex;
    padding: 2px;
  }
`;

export default function DensityHeatmapCard({ slice, filters, typeColors }) {
  const { t, i18n } = useTranslation();
  const data = slice.data;
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDetail, setDayDetail] = useState({ loading: false, items: [], total: 0 });

  const todayStr = new Date().toDateString();

  const monthDays = useMemo(() => {
    if (!data) return [];
    const byDay = new Map((data.days || []).map((d) => [new Date(d.date).getDate(), d]));
    const count = new Date(data.year, data.month, 0).getDate();
    return Array.from({ length: count }, (_, i) => {
      const dayNo = i + 1;
      const dateObj = new Date(data.year, data.month - 1, dayNo);
      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
      return { dayNo, dateObj, isWeekend, info: byDay.get(dayNo) || null };
    });
  }, [data]);

  const openDay = useCallback(async (dateObj) => {
    const iso = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
    setSelectedDay(dateObj);
    setDayDetail({ loading: true, items: [], total: 0 });
    try {
      const res = await getTodayOnLeave({ date: iso, limit: 50, ...filters });
      const payload = res.data?.data;
      setDayDetail({ loading: false, items: payload?.items || [], total: payload?.totalCount || 0 });
    } catch {
      setDayDetail({ loading: false, items: [], total: 0 });
    }
  }, [filters]);

  const title = data
    ? `${new Date(data.year, data.month - 1, 1).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })} — ${t('leaveDashboard.heatmap.title')}`
    : t('leaveDashboard.heatmap.title');

  const fmtLong = (d) => d.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', weekday: 'long' });

  return (
    <DashboardCard
      cardKey="izin-yogunluk-haritasi"
      title={title}
      span={12}
      spanMd={6}
      loading={slice.loading}
      empty={!data || (data.days || []).length === 0}
      domId="lvd-density"
      headerExtra={(
        <LegendWrap data-export-keep>
          <TodayPill>{t('leaveDashboard.heatmap.today')}</TodayPill>
          <LegendKey>
            {t('leaveDashboard.heatmap.legendLow')}
            {HEAT_RAMP.map((c) => <RampSwatch key={c} $color={c} />)}
            {t('leaveDashboard.heatmap.legendHigh')}
          </LegendKey>
          <LegendKey><RampSwatch $color={STATUS.critical} /> {t('leaveDashboard.heatmap.critical')}</LegendKey>
          <LegendKey><HatchSwatch /> {t('leaveDashboard.heatmap.pending')}</LegendKey>
        </LegendWrap>
      )}
    >
      <div>
        <Strip>
          {monthDays.map(({ dayNo, dateObj, isWeekend, info }) => {
            const isToday = dateObj.toDateString() === todayStr;
            let bg = HEAT_EMPTY;
            let tooltip = dateObj.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
            if (isWeekend) {
              bg = '#F7F9FB';
            } else if (info) {
              bg = info.isCriticalOverlap ? STATUS.critical : heatColor(info.approvedCount, data.maxApprovedCount);
              if (info.isHoliday) bg = '#D8DDE4';
              tooltip += ` — ${t('leaveDashboard.heatmap.onLeaveCount', { count: info.approvedCount })}`;
              if (info.pendingCount > 0) tooltip += ` · ${t('leaveDashboard.heatmap.pendingCount', { count: info.pendingCount })}`;
              if (info.isHoliday && info.holidayName) tooltip += ` · ${info.holidayName}`;
            }
            return (
              <DayCol
                key={dayNo}
                type="button"
                disabled={isWeekend || !info}
                title={tooltip}
                onClick={() => info && openDay(dateObj)}
              >
                <DayNum $muted={isWeekend} $today={isToday}>{dayNo}</DayNum>
                <Cell $bg={bg} $today={isToday} $pending={!isWeekend && info?.pendingCount > 0} />
              </DayCol>
            );
          })}
        </Strip>
        <FooterRow>
          <Footnote>{t('leaveDashboard.heatmap.workdaysNote')}</Footnote>
          <Footnote>{t('leaveDashboard.heatmap.clickNote')}</Footnote>
        </FooterRow>
        {selectedDay && (
          <DayPanel>
            <DayPanelHeader>
              <span>
                {fmtLong(selectedDay)}
                {' · '}
                {t('leaveDashboard.heatmap.onLeaveCount', { count: dayDetail.total })}
              </span>
              <button type="button" onClick={() => setSelectedDay(null)} aria-label={t('leaveDashboard.heatmap.close')}>
                <X size={15} />
              </button>
            </DayPanelHeader>
            {dayDetail.loading ? (
              <RowSub>{t('leaveDashboard.loading')}</RowSub>
            ) : dayDetail.items.length === 0 ? (
              <RowSub>{t('leaveDashboard.heatmap.dayEmpty')}</RowSub>
            ) : dayDetail.items.map((item) => (
              <ListRow key={item.leaveId}>
                <RowAvatar src={item.profilePicture} name={item.fullName} initials={initialsOf(item.fullName)} />
                <RowMain>
                  <RowName>{item.fullName}</RowName>
                  <RowSub>{[item.department, item.location].filter(Boolean).join(' · ')}</RowSub>
                </RowMain>
                <TypeChip $bg={typeColors.get(item.leaveVariationId) || TYPE_FOLD_COLOR}>
                  {item.leaveVariationName}
                </TypeChip>
              </ListRow>
            ))}
          </DayPanel>
        )}
      </div>
    </DashboardCard>
  );
}
